<template>
  <div class="detection-management">
    <!-- 第一行（提示文字、功能按钮、数据显示设置） -->
    <div class="first-row">
      <div>
        <span style="font-size: 14px; color: #333">检测管理</span>
      </div>
      <div class="button-group">
        <!-- 下载模板 -->
        <el-button
          class="custom-button-spacing"
          type="primary"
          plain
          @click="downloadTemplate"
        >
          <el-icon><Download /></el-icon>
          下载模板
        </el-button>
        <!-- 导入按钮 -->
        <el-upload
          v-if="userRole === 0 || userRole === 2"
          type="primary"
          plain
          class="upload-demo"
          :auto-upload="false"
          :show-file-list="false"
          @change="handleImportSuccess"
          name="file"
        >
          <el-button type="success" class="custom-button-spacing">
            <el-icon class="custom-icon-spacing"><FolderAdd /></el-icon>
            导入
          </el-button>
        </el-upload>
        <!-- 刷新按钮 -->
        <el-button type="text" @click="refreshData" class="custom-button-spacing">
          <el-icon><Refresh /></el-icon>
        </el-button>

        <!-- 设置按钮 -->
        <el-popover placement="bottom" width="160" v-model:visible="densityDialogVisible">
          <div class="popover-content">
            <el-button
              class="density-button"
              :type="rowHeight === '80px' ? 'primary' : ''"
              @click="setRowHeight('80px')"
            >
              默认
            </el-button>
            <el-button
              class="density-button"
              :type="rowHeight === '60px' ? 'primary' : ''"
              @click="setRowHeight('60px')"
            >
              中等
            </el-button>
            <el-button
              class="density-button"
              :type="rowHeight === '30px' ? 'primary' : ''"
              @click="setRowHeight('30px')"
            >
              紧凑
            </el-button>
          </div>
          <template #reference>
            <el-button type="text" class="custom-button-spacing">
              <el-icon><Grid /></el-icon>
            </el-button>
          </template>
        </el-popover>

        <!-- 列显示按钮 -->
        <el-popover placement="bottom" width="100" v-model:visible="columnsDialogVisible">
          <div class="popover-content">
            <el-checkbox-group v-model="selectedColumns">
              <el-checkbox
                v-for="column in columns"
                :key="column.prop"
                :label="column.prop"
              >
                {{ column.label }}
              </el-checkbox>
            </el-checkbox-group>
            <div class="popover-footer">
              <el-button @click="resetColumns">重置</el-button>
              <el-button type="primary" @click="confirmColumns">确定</el-button>
            </div>
          </div>
          <template #reference>
            <el-button type="text">
              <el-icon><Setting /></el-icon>
            </el-button>
          </template>
        </el-popover>
      </div>
    </div>

    <!-- 数据表格 -->
    <el-table
      :data="tableData"
      style="width: 98.5%; margin-left: 10px; margin-right: 20px"
      :row-class-name="tableRowClassName"
      :default-expand-all="false"
      :header-cell-style="{
        background: '#f5f7fa',
        color: '#909399',
        textAlign: 'center',
      }"
      :row-style="getRowStyle"
      :cell-style="{ textAlign: 'center' }"
    >
      <!-- 动态生成列 -->
      <template v-for="column in columns" :key="column.prop">
        <el-table-column
          v-if="column.visible"
          :prop="column.prop"
          :label="column.label"
          :formatter="column.formatter"
          :filters="column.filters"
          :filter-method="column.filterMethod"
          :filter-placement="column.filterPlacement"
          align="center"
        >
          <template #header="scope">
            {{ scope.column.label }}
            <i
              v-if="column.filters"
              class="el-icon-filter"
              style="cursor: pointer; color: #409eff"
              @click.stop="showFilter(column.prop)"
            ></i>
          </template>
          <template #default="scope">
            <template v-if="column.prop === 'connectionStatus'">
              <span>
                <i :class="`status-dot ${getStatusClass(scope.row[column.prop])}`"></i>
                {{ getConnectionStatus(scope.row[column.prop]) }}
              </span>
            </template>

            <template v-if="column.prop === 'detectionStatus'">
              <span>
                <i :class="`status-dot ${getStatusClass(scope.row[column.prop])}`"></i>
                {{ getDetectionStatus(scope.row[column.prop]) }}
              </span>
            </template>
            
            <!-- <template v-else>
              {{ scope.row[column.prop] }}
            </template> -->
          </template>
        </el-table-column>
      </template>

      <template #empty>
        <div class="empty-state">
          <el-icon class="empty-icon"><Box /></el-icon>
          <p class="empty-text">暂无数据</p>
        </div>
      </template>
    </el-table>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { getDevicesForDetection, downloadDeviceTemplate, importDevicesData } from "@/api/device";
import { Refresh, Setting, Grid, FolderAdd, Download, Box } from "@element-plus/icons-vue"; // 新增引入

const initialColumns = [
  { prop: "index", label: "序号", visible: true },
  { prop: "mad", label: "MAD", visible: true },
  { prop: "date", label: "出厂日期", visible: true },
  { prop: "type", label: "设备类型", visible: true },
  {
    prop: "connectionStatus",
    label: "设备连接状态",
    visible: true,
    filters: [
      { text: "在线", value: 1 },
      { text: "离线", value: 0 },
    ],
    filterMethod: (value, row) => row.connectionStatus === value,
    filterPlacement: "bottom-end",
  },
  {
    prop: "detectionStatus",
    label: "设备检测状态",
    visible: true,
    filters: [
      { text: "未检测", value: 2 },
      { text: "成功", value: 1 },
      { text: "失败", value: 0 },
    ],
    filterMethod: (value, row) => row.detectionStatus === value,
    filterPlacement: "bottom-end",
  },
];

export default {
  name: "DetectionManagement",
  components: {
    Refresh,
    Setting,
    Grid,
    FolderAdd,
    Download,
    Box,
  },
  setup() {
    const userRole = ref(null);

    onMounted(() => {
      // 从localStorage获取用户角色
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      userRole.value = userInfo ? userInfo.role : null;
      // 页面加载时获取数据
      refreshData(false);
    });

    const tableData = ref([]);

    // 列显示配置
    const columns = ref([...initialColumns]);
    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const columnsDialogVisible = ref(false);

    // 行间距配置
    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);

    // 表格行样式
    const getRowStyle = () => {
      return { height: rowHeight.value };
    };

    const tableRowClassName = ({ rowIndex }) => {
      if (rowIndex % 2 === 0) {
        return "even-row";
      } else {
        return "odd-row";
      }
    };

    // 状态样式
    const getStatusClass = (status) => {
      switch (status) {
        case 1:
          return "status-dot-green";
        case 0:
          return "status-dot-red";
        case 2:
          return "status-dot-purple";
        default:
          return "status-dot-black";
      }
    };

    const getConnectionStatus = (status) => {
      switch (status) {
        case 0:
          return "未连接";
        case 1:
          return "已连接";
        default:
          return "未连接";
      }
    };

    const getDetectionStatus = (status) => {
      switch (status) {
        case 0:
          return "失败";
        case 1:
          return "成功";
        case 2:
          return "未检测";
      }
    };

    // 刷新数据
    const refreshData = async (isShow) => {
      try {
        const response = await getDevicesForDetection();
        if (response.success) {
          tableData.value = response.data;
          if(isShow)
            ElMessage.success("数据已刷新");
        }
      } catch (error) {
        ElMessage.error("刷新数据失败");
      }
    };

    // 导入功能
    const beforeUpload = (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";

      if (!isExcel) {
        ElMessage.error("只能上传 Excel 文件");
        return false;
      }

      return true;
    };

    const handleImportSuccess = async (file) => {
  try {
    const formData = new FormData();
    // 正确获取文件对象（注意：file.raw 是上传的文件对象）
    formData.append('file', file.raw); 
    console.log('Uploaded file:', file.raw); // 检查是否有 File 对象
    console.log('FormData entries:', [...formData.entries()]); // 检查 FormData 内容
    const response = await importDevicesData(formData);
    if (response.success) {
      ElMessage.success("导入成功");
      refreshData(true);
    } else {
      ElMessage.error(response.message || "导入失败");
    }
  } catch (error) {
    ElMessage.error("导入失败: " + (error.message || "未知错误"));
  }
};

    // 导出功能
    const downloadTemplate = async () => {
      try {
        const response = await downloadDeviceTemplate();
        const blob = new Blob([response], { type: "application/vnd.ms-excel" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "设备模板.xlsx";
        link.click();
      } catch (error) {
        ElMessage.error("下载模板失败");
      }
    };

    // 列显示设置
    const showColumnsDialog = () => {
      columnsDialogVisible.value = true;
    };

    const resetColumns = () => {
      selectedColumns.value = columns.value.map((col) => col.prop);
    };

    const confirmColumns = () => {
      columns.value.forEach((col) => {
        col.visible = selectedColumns.value.includes(col.prop);
      });
      columnsDialogVisible.value = false;
    };

    // 密度调整
    const showDensityDialog = () => {
      densityDialogVisible.value = true;
    };

    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    return {
      userRole,
      tableData,
      columns,
      selectedColumns,
      columnsDialogVisible,
      rowHeight,
      densityDialogVisible,
      getRowStyle,
      tableRowClassName,
      getStatusClass,
      getConnectionStatus,
      getDetectionStatus,
      refreshData,
      beforeUpload,
      handleImportSuccess,
      downloadTemplate,
      showColumnsDialog,
      resetColumns,
      confirmColumns,
      showDensityDialog,
      setRowHeight,
    };
  },
};
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.empty-icon {
  font-size: 48px;
  color: #909399;
  margin-bottom: 10px;
}

.empty-text {
  color: #909399;
  font-size: 14px;
  margin: 0;
}

.detection-management {
  background-color: white; /* 整体背景为灰色 */
}

.first-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  margin-left: 10px;
  margin-right: 10px;
}

.button-group {
  display: flex;
  align-items: center;
  padding: auto;
  margin-top: 10px;
}

.el-table .cell {
  padding: 0 !important;
}

.even-row {
  background-color: #f9fafc;
}

.odd-row {
  background-color: #f0f0f0; /* 奇数行背景为灰色 */
}

/* 状态小圆点样式 */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}

.status-dot-green {
  background-color: green;
}

.status-dot-red {
  background-color: red;
}

.status-dot-purple {
  background-color: purple;
}

.status-dot-black {
  background-color: black;
}

/* 按钮样式 */
.el-icon-menu {
  font-size: 18px;
  cursor: pointer;
}

.el-icon-refresh {
  font-size: 18px;
}
.custom-icon-spacing {
  margin-right: 4px;
}
.custom-button-spacing {
  margin-right: 15px;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  display: flex;
  justify-content: center; /* 水平居中按钮 */
  margin-top: auto; /* 将 footer 推到底部 */
  padding: 10px; /* 可根据需要调整内边距 */
}

.density-button {
  display: flex; /* 使用 Flexbox 布局 */
  justify-content: center; /* 水平居中 */
  margin: 10px; /* 按钮之间的间距 */
}

</style>
